import { Link, useLocation } from 'react-router-dom';
import { searchContext } from '../../../../context/Context';
import { useMappingDrillDown } from '../../../../context/MappingDrillDownContext';
import { DATASETS } from '../../../Constants';
import updateParams from '../../../../utils/updateParams';

function resolveGeneId(data: any): string | number | undefined {
  return (
    data?.geneID ??
    data?.ncbiID ??
    data?.geneNcbiID ??
    data?.gene_ncbi_id ??
    (Array.isArray(data?.geneNcbiIDs) ? data.geneNcbiIDs[0] : data?.geneNcbiIDs)
  );
}

function resolveVariantId(data: any): string | undefined {
  return data?.strID ?? data?.variantStrID;
}
import dropdownLists from '../../DropdownLists';
import { getPmidLink } from '../renders/MappingFunctions';
import { renderSentenceHTML, renderNCTid } from '../renders/CommonAtributes';
import { ScoreHeader } from '../components/index';
import { getHeaderProps, getCategoryByTab } from '../config/helpers';
import { renderReferenceLink } from '../renders/linkRenderers';
import { renderMappingButton } from '../renders/buttonRenderers';
import { createSelectFilter } from '../filters/filterConfigs';
import RestrictedContent from '../../components/RestrictedContent';
import RestrictedHeader from '../../components/RestrictedHeader';
import { formatAssociationType } from '@/utils/formatAssociationType';
import { renderNumericColumn, renderTextColumn } from '@/utils/renderTableValue';
import { ScoreInfoButton } from '../components/ScoreInfoButton';
import EvidenceTimelineCellButton from '../components/EvidenceTimelineCellButton';

export const getAssociationColumns = context => {
  const { tab, isGDA, isVDA, isAcademic, isAuthenticated, isEvidence } = context;
  const headerProps = getHeaderProps();

  const isChemicalRestricted = isAuthenticated && isAcademic;

  return [
    ...(!isEvidence && (isGDA || isVDA)
      ? [
          {
            name: 'evidenceTimeline',
            category: isGDA ? 'GDA' : 'VDA',
            sortable: false,
            header: (
              <span className="evidence-timeline-column-header" title="Evidence by year (click for details)">
                Evidence timeline
              </span>
            ),
            headerProps,
            minWidth: 190,
            render: ({ data }) => <EvidenceTimelineCellButton rowData={data} />,
          },
        ]
      : []),
    {
      name: 'associationType',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="Association Type">Association Type</span>,
      ...createSelectFilter(
        isGDA ? dropdownLists.associationTypeGda || [] : dropdownLists.associationTypeVda || [],
      ),
      headerProps,
      minWidth: 170,
      render: ({ value }) => formatAssociationType(renderTextColumn(value)),
    },

    {
      name: 'EI',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      header: (
        <span title={`Evidence Index for the ${isGDA ? 'GDA' : isVDA ? 'VDA' : ''}`}>
          EI<sub>{isGDA ? 'gda' : isVDA ? 'vda' : ''}</sub>
        </span>
      ),
      headerProps,
      filterEditorProps: { minValue: 0, maxValue: 1, step: 0.01 },
      minWidth: 85,
      render: ({ data }) => {
        const result = renderNumericColumn(data?.EI);
        if (result === '0') return '0.00';
        return Number(result).toFixed(2);
      },
    },

    {
      name: 'EL',
      category: 'GDA',
      sortable: true,
      header: (
        <span title="Evidence Level for the GDA">
          EL<sub>gda</sub>
        </span>
      ),
      ...createSelectFilter(dropdownLists.EL || []),
      headerProps,
      minWidth: 115,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'score',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <ScoreHeader />,
      headerProps,
      filterEditorProps: { minValue: 0, maxValue: 1, step: 0.01 },
      minWidth: 150,
      render: ({ value, data }) =>
        !isEvidence ? (
          <ScoreInfoButton data={data} value={value} />
        ) : (
          <span>{renderNumericColumn(value)}</span>
        ),
    },

    {
      name: 'source',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="Database reporting the association">Original DB</span>,
      headerProps,
      minWidth: 130,
      ...createSelectFilter(isGDA ? dropdownLists.gdaSourcesList || [] : dropdownLists.vdaSourcesList || []),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'sentenceHTML',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: false,
      header: <span title="Sentence supporting the association">Sentence</span>,
      textEllipsis: true,
      headerProps,
      minWidth: 520,
      defaultFlex: 1,
      render: instance => {
        const sentence = instance?.data?.sentenceHTML;
        return sentence ? renderSentenceHTML('sentenceHTML', instance) : '-';
      },
    },

    {
      name: 'pmid',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      header: <span title="PubMed Identifier">PMID</span>,
      headerProps,
      minWidth: 125,
      render: ({ data }) => {
        const result = renderNumericColumn(data?.pmid);
        if (result === '0') return '-';
        return getPmidLink(result);
      },
    },

    {
      name: 'reference',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="reference type">Reference</span>,
      headerProps,
      render: ({ data }) => {
        const result = renderTextColumn(data?.reference);
        if (result === '-') return '-';
        return <>{renderReferenceLink(data)}</>;
      },
    },

    {
      name: 'reference_type',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="reference type">Reference Type</span>,
      headerProps,
      ...createSelectFilter(dropdownLists.reference_type || []),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'pmYear',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="Publication Year">Reference Year</span>,
      headerProps,
      minWidth: 158,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'journal_abbrv',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="Name of the Journal">Journal</span>,
      headerProps,
      minWidth: 295,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'firstRef',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      minWidth: 150,
      header: <span title="First time the disease was reported">First Ref.</span>,
      headerProps,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'lastRef',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      minWidth: 150,
      header: <span title="Last time the disease was reported">Last Ref.</span>,
      headerProps,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'yearInitial',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      header: <span title="First time the disease was reported">First Ref.</span>,
      headerProps,
      minWidth: 150,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'yearFinal',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      header: <span title="Last time the association was reported">Last Ref.</span>,
      headerProps,
      minWidth: 150,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'nct_id',
      category: getCategoryByTab(tab),
      sortable: false,
      header: <span title="NCT ID">NCT ID</span>,
      headerProps,
      minWidth: 295,
      render: ({ data }) => {
        const result = renderTextColumn(data?.nct_id);
        if (result === '-') return '-';
        return renderNCTid({ data });
      },
    },

    {
      name: 'numPMIDsupportingAssociation',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,

      header: (
        <span title="Number of Publications supporting the association">
          {(() => {
            const { view, tab } = searchContext();
            const isChemicalView = view === DATASETS.CHEMICALS;

            return isChemicalView && (tab === 'GDA' || tab === 'VDA') ? 'N PMIDs Chemicals' : 'N PMIDs';
          })()}
        </span>
      ),

      headerProps,
      minWidth: 130,

      render: ({ data }) => {
        const { view, tab, searchItems } = searchContext();
        const { effectiveTab } = useMappingDrillDown();
        const resolvedTab = effectiveTab || tab;

        let mappingItems: any[] = [];
        let columVisibilityModelKey = '';
        let tabSegment = '';

        const isChemicalView = view === DATASETS.CHEMICALS;

        const displayValue =
          isChemicalView && resolvedTab === 'GDA'
            ? data?.numberPmidsWithChemsIncludedInEvidence
            : data?.numPMIDsupportingAssociation;

        if (isChemicalView) {
          const chemOfInterest = searchItems.map(item => ({
            filterParam: 'chem_ids_filter',
            filterValue: item?.chemID,
            name: item?.chemPrefName,
            id: item?.chemID,
          }))?.[0];

          if (!chemOfInterest) return null;

          if (resolvedTab === 'GDA') {
            columVisibilityModelKey = 'numPMIDsupportingAssociationGDA';
            tabSegment = 'gda';

            mappingItems = [
              {
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
                name: data.diseaseName,
                id: data.diseaseUMLSCUI,
              },
              {
                filterParam: 'gene_ncbi_id',
                filterValue: data.geneNcbiID,
                name: data.geneSymbol,
                id: data.geneDescription,
              },
              chemOfInterest,
            ];
          }

          if (resolvedTab === 'VDA') {
            columVisibilityModelKey = 'numPMIDsupportingAssociationVDA';
            tabSegment = 'vda';

            mappingItems = [
              {
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
                name: data.diseaseName,
                id: data.diseaseUMLSCUI,
              },
              {
                filterParam: 'variant',
                filterValue: data.variantStrID,
                name: data.variantStrID,
                id: data.geneSymbol,
              },
              chemOfInterest,
            ];
          }
        } else {
          if (resolvedTab === 'GDA') {
            columVisibilityModelKey = 'numPMIDsupportingAssociationGDA';
            tabSegment = 'gda';

            mappingItems = [
              {
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
                name: data.diseaseName,
                id: data.diseaseUMLSCUI,
              },
              {
                filterParam: 'gene_ncbi_id',
                filterValue: data.geneNcbiID,
                name: data.geneSymbol,
                id: data.geneDescription,
              },
            ];
          }

          if (resolvedTab === 'VDA') {
            columVisibilityModelKey = 'numPMIDsupportingAssociationVDA';
            tabSegment = 'vda';

            mappingItems = [
              {
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
                name: data.diseaseName,
                id: data.diseaseUMLSCUI,
              },
              {
                filterParam: 'variant',
                filterValue: data.variantStrID,
                name: data.variantStrID,
                id: data.geneSymbol,
              },
            ];
          }
        }

        return renderMappingButton(data, {
          displayValue,
          mappingItems,
          columVisibilityModelKey,
          tabSegment,
          name: 'numPMIDsupportingAssociation',
        });
      },
    },

    {
      name: 'numPublications',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      header: <span title="Number of Publications mentioning the disease">N PMIDs</span>,
      headerProps,
      render: ({ data }) => {
        const { tab } = searchContext();
        const location = useLocation();
        let newParams = '';

        if (tab === 'GENES') {
          newParams = updateParams({
            view: DATASETS.GENES,
            tab: 'GDA',
            searchItems: [{ id: String(resolveGeneId(data) ?? '') }],
          });
        } else if (tab === 'VARIANTS') {
          newParams = updateParams({
            view: DATASETS.VARIANTS,
            tab: 'VDA',
            searchItems: [{ id: resolveVariantId(data) }],
          });
        } else if (tab === 'DISEASES') {
          return data.numPublications;
        }

        if (tab === 'VARIANTS') {
          const nRaw = Number(data?.numPublications);
          const n = Number.isFinite(nRaw) ? nRaw : 0;
          if (n === 0) {
            return 0;
          }
        }

        const newUrl = `${location.pathname}?${newParams}`;
        return (
          <Link className="tab-links" to={newUrl}>
            {data.numPublications}{' '}
          </Link>
        );
      },
    },

    {
      name: 'numPublications_GDA',
      category: getCategoryByTab(tab),
      sortable: true,
      header: <span title="Number of Publications mentioning the disease-gene association">N PMIDs GDA</span>,
      headerProps,
      render: ({ data }) => {
        const { tab } = searchContext();
        const location = useLocation();
        let newParams = '';

        if (tab === 'DISEASES') {
          newParams = updateParams({
            view: DATASETS.DISEASES,
            tab: 'GDA',
            searchItems: [{ id: data.diseaseUMLSCUI }],
          });
        }

        const newUrl = `${location.pathname}?${newParams}`;
        return (
          <Link className="tab-links" to={newUrl}>
            {data.numPublications_GDA}{' '}
          </Link>
        );
      },
    },

    {
      name: 'numPublications_VDA',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: (() => {
        const { source } = searchContext();
        return source !== 'ORPHANET';
      })(),
      header: (
        <span title="Number of Publications mentioning the disease-variant association">N PMIDs VDA</span>
      ),
      headerProps,
      render: ({ data }) => {
        const { tab } = searchContext();
        const location = useLocation();
        let newParams = '';

        if (tab === 'DISEASES') {
          newParams = updateParams({
            view: DATASETS.DISEASES,
            tab: 'VDA',
            searchItems: [{ id: data.diseaseUMLSCUI }],
          });
        }

        const newUrl = `${location.pathname}?${newParams}`;
        return (
          <Link className="tab-links" to={newUrl}>
            {data.numPublications_VDA}{' '}
          </Link>
        );
      },
    },

    {
      name: 'numDBSNPsupportingAssociation',
      category: 'GDA',
      sortable: true,
      header: (
        <span title="Number of Variants for the Gene-Disease Association">
          N variants<sub>gda</sub>
        </span>
      ),
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const { tab } = searchContext();
        const { effectiveTab } = useMappingDrillDown();
        const resolvedTab = effectiveTab || tab;

        if (resolvedTab !== 'GDA') return null;

        const config = {
          displayValue: data.numDBSNPsupportingAssociation,
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'gene_ncbi_id',
              filterValue: data.geneNcbiID,
              name: data.geneSymbol,
              id: data.geneDescription,
            },
          ],
          columVisibilityModelKey: 'GENES_VDA',
          tabSegment: 'vda',
          name: 'numDBSNPsupportingAssociation',
        };

        return renderMappingButton(data, config);
      },
    },

    {
      name: 'numberChemsIncludedInEvidence',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Number of Chemicals Included in the Evidences">N Chemicals</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 130,
      render: ({ data }) => {
        const { tab } = searchContext();
        const { effectiveTab } = useMappingDrillDown();
        const resolvedTab = effectiveTab || tab;

        let mappingItems = [];
        let columVisibilityModelKey = '';
        let tabSegment = '';

        if (resolvedTab === 'GDA') {
          columVisibilityModelKey = 'numberChemsIncludedInEvidenceGDA';
          tabSegment = 'chem/gda';
          mappingItems = [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'gene_ncbi_id',
              filterValue: data.geneNcbiID,
              name: data.geneSymbol,
              id: data.geneDescription,
            },
          ];
        } else if (resolvedTab === 'VDA') {
          columVisibilityModelKey = 'numberChemsIncludedInEvidenceVDA';
          tabSegment = 'chem/vda';
          mappingItems = [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'variant',
              filterValue: data.variantStrID,
              name: data.variantStrID,
              id: data.geneSymbol,
            },
          ];
        }

        if (!data.numberChemsIncludedInEvidence || data.numberChemsIncludedInEvidence === 0) {
          return <RestrictedContent isRestricted={isChemicalRestricted}>0</RestrictedContent>;
        }

        const config = {
          displayValue: data.numberChemsIncludedInEvidence || '0',
          mappingItems,
          columVisibilityModelKey,
          tabSegment,
          name: 'numberChemsIncludedInEvidence',
          viewSegment: '/all',
        };

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {renderMappingButton(data, config)}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'association',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="">Polarity</span>,
      headerProps,
      ...createSelectFilter(dropdownLists.association || []),
      minWidth: 85,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'clinical_significance',
      category: isGDA ? 'GDA' : isVDA ? 'VDA' : getCategoryByTab(tab),
      sortable: true,
      header: <span title="">Clinical Significance</span>,
      headerProps,
      minWidth: 215,
      ...createSelectFilter(dropdownLists.clinical_significance || []),
      render: ({ value }) => renderTextColumn(value),
    },
    {
      name: 'icite_apt',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="Approximate potential to translate">Translation</span>,
      textEllipsis: true,
      headerProps,
      minWidth: 150,
      defaultFlex: 1,
      render: ({ value }) => renderNumericColumn(value),
    },

    // VDA specific columns
    {
      name: 'gof_lof',
      category: 'VDA',
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="GOF / LOF">GOF / LOF</span>
        </RestrictedHeader>
      ),
      ...createSelectFilter(dropdownLists.gof_lof || []),
      headerProps,
      minWidth: 115,
      render: ({ data }) => {
        const result = renderTextColumn(data?.gof_lof);
        return <RestrictedContent isRestricted={isChemicalRestricted}>{result}</RestrictedContent>;
      },
    },

    {
      name: 'riskAllele',
      category: 'VDA',
      sortable: true,
      header: <span title="Risk Allele">Risk Allele</span>,
      headerProps,
      minWidth: 125,
      ...createSelectFilter(dropdownLists.riskAllele || []),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'pValue',
      category: 'VDA',
      sortable: true,
      header: <span title="P-value of the GWAS">pValue</span>,
      headerProps,
      minWidth: 95,
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'ancestry_name',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="ancestry_name">Ancestry</span>,
      textEllipsis: true,
      headerProps,
      minWidth: 260,
      defaultFlex: 1,
      ...createSelectFilter(dropdownLists.ancestry_name || []),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'beta',
      category: 'VDA',
      sortable: true,
      header: <span title="beta">Beta</span>,
      textEllipsis: true,
      headerProps,
      minWidth: 160,
      defaultFlex: 1,
      render: ({ value }) => renderNumericColumn(value),
    },

    {
      name: 'gender',
      category: 'VDA',
      sortable: true,
      header: <span title="gender">Gender</span>,
      textEllipsis: true,
      headerProps,
      minWidth: 160,
      defaultFlex: 1,
      ...createSelectFilter(dropdownLists.gender || []),
      render: ({ value }) => renderTextColumn(value),
    },

    {
      name: 'odds_ratio',
      category: 'VDA',
      sortable: true,
      header: <span title="odds_ratio">Odds Ratio</span>,
      textEllipsis: true,
      headerProps,
      minWidth: 150,
      defaultFlex: 1,
      render: ({ value }) => renderNumericColumn(value),
    },

    {
      name: 'text',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: false,
      header: <span title="Title">Title</span>,
      textEllipsis: true,
      headerProps,
      minWidth: 520,
      defaultFlex: 1,
      render: instance => {
        const sentence = instance?.data?.text;
        return sentence ? renderSentenceHTML('text', instance) : '-';
      },
    },
  ];
};
